import React, {Component} from 'react';
import Tree from "rc-tree/es";
import classNames from 'classnames';

interface TreeNodeProps {
    key?: string,
    title?: string,
    children: TreeNodeProps[],
    expand: boolean,
    checked: boolean,
    disabled: boolean,
    className: string,
    checkable: Boolean,
}

interface TreeRootProps {
    checkedKeys: [], // default keys
    nodes: TreeNodeProps[],
    // onExpand: Function,
    name: string,
    onCheck: Function,
}

const Icon = ({selected}) => (
    <span className={classNames('mdi font-18', {'mdi-folder': !selected, 'mdi-folder-open': selected})}/>);

const CheckIcon = ({expanded, isLeaf}) => {
    return (
        <span
            className={classNames('mdi', {
                'mdi-chevron-right': !isLeaf && !expanded,
                'mdi-chevron-down': expanded,
                '': isLeaf,
            })}/>)
}

const getAllChildKeys = (nodes) => {
    let keys = [];
    for (const node of nodes) {
        keys.push(node.key);
        if (node.children && node.children.length > 0) {
            keys = keys.concat(getAllChildKeys(node.children));
        }
    }
    return keys;
};

const findNodeByKey = (nodes, key) => {
    for (const node of nodes) {
        if (node.key === key) return node;
        if (node.children) {
            const found = findNodeByKey(node.children, key);
            if (found) return found;
        }
    }
    return null;
};

const computeCheckedState = (checkedKeys, nodes) => {
    const checked = [];
    const halfChecked = [];
    const checkedSet = new Set(checkedKeys);

    const traverse = (nodeList) => {
        for (const node of nodeList) {
            if (node.children && node.children.length > 0) {
                const childKeys = getAllChildKeys(node.children);
                const someChecked = childKeys.some(k => checkedSet.has(k));
                const allChecked = childKeys.length > 0 && childKeys.every(k => checkedSet.has(k));

                if (allChecked) {
                    checked.push(node.key);
                } else if (someChecked) {
                    halfChecked.push(node.key);
                }

                traverse(node.children);
            } else {
                if (checkedSet.has(node.key)) {
                    checked.push(node.key);
                }
            }
        }
    };

    traverse(nodes);
    return { checked, halfChecked };
};

class BasicTree extends Component<TreeRootProps> {

    constructor(props) {
        super(props);
        const {nodes, checkedKeys, onCheck} = props;

        this.state = {
            disabled: false,
            checkable: true,
            defaultCheckedKeys: checkedKeys,
            nodes,
            onCheck,
        };
    }

    static getDerivedStateFromProps(props, state) {
        state.disabled = props.disabled === undefined ? false : props.disabled;
        state.checkable = props.checkable === undefined ? true : props.checkable;
        return {
            ...state
        }
    }

    handleOnCheck = (checkedKeysValue, info) => {
        const {checked} = checkedKeysValue;
        const {node, checked: isChecking} = info;
        const nodes = this.props.nodes;

        let newChecked = [...checked];

        if (node.children && node.children.length > 0) {
            const childKeys = getAllChildKeys(node.children);
            if (isChecking) {
                childKeys.forEach(key => {
                    if (!newChecked.includes(key)) {
                        newChecked.push(key);
                    }
                });
            } else {
                newChecked = newChecked.filter(key => !childKeys.includes(key));
            }
        }

        this.props.onCheck(this.props.name, newChecked);
    };

    render() {
        const checkedKeys = this.props.checkedKeys || [];
        const nodes = this.props.nodes || [];
        const { checked, halfChecked } = computeCheckedState(checkedKeys, nodes);

        return (
            <Tree
                showLine={true}
                checkable={this.state.checkable}
                checkStrictly={true}
                multiple={true}
                selectable={false}
                disabled={this.state.disabled}
                defaultExpandAll={false}
                defaultExpandParent={false}
                checkedKeys={{checked, halfChecked}}
                onCheck={this.handleOnCheck}
                treeData={nodes}
                icon={Icon}
                switcherIcon={CheckIcon}
            >
            </Tree>
        );
    }
}

export {
    BasicTree,
    TreeRootProps,
    TreeNodeProps
}
